/* eslint-disable react/prop-types */
import { useState, useEffect } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Plus, Pencil, Trash, UsersThree, Phone, EnvelopeSimple, Buildings, IdentificationBadge, MapPin,
} from "@phosphor-icons/react";

/**
 * Sidebar card listing every "party" (anyone involved in the transaction
 * who isn't already a client/solicitor/referrer of this firm). Solicitors
 * can add / edit / delete; clients see a read-only view (no edit buttons)
 * but the section is still visible so they know who else is in the chain.
 *
 * Backend: `/api/cases/{caseId}/parties` CRUD; common-role suggestions
 * served from `/api/cases/parties/common-roles` so the autocomplete stays
 * in sync without duplicating the list client-side.
 */
const ROLE_COLORS = {
  "Other side (buyer)": "bg-blue-50 text-blue-700",
  "Other side (seller)": "bg-amber-50 text-amber-700",
  "Other side's solicitor": "bg-violet-50 text-violet-700",
  "Estate agent": "bg-emerald-50 text-emerald-700",
  "Buying agent": "bg-emerald-50 text-emerald-700",
  "Mortgage broker": "bg-rose-50 text-rose-700",
  "Lender": "bg-rose-50 text-rose-700",
  "Surveyor": "bg-cyan-50 text-cyan-700",
};

export default function PartiesCard({ caseId, isSolicitor }) {
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);   // {party?, mode: "add"|"edit"}
  const [expandedId, setExpandedId] = useState(null);

  const fetchParties = async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/${caseId}/parties`);
      setParties(Array.isArray(data) ? data : []);
    } catch (err) {
      // Silent — parties is a non-critical section.
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { if (caseId) fetchParties(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [caseId]);

  // Lazy-load role suggestions only when the dialog is first opened — saves
  // a request on every case open for clients who'll never edit.
  useEffect(() => {
    if (editing && isSolicitor && roles.length === 0) {
      API.get("/cases/parties/common-roles").then(({ data }) => setRoles(data)).catch(() => {});
    }
  }, [editing, isSolicitor, roles.length]);

  const handleDelete = async (party) => {
    if (!window.confirm(`Remove ${party.name || party.firm || party.role}?`)) return;
    try {
      await API.delete(`/cases/${caseId}/parties/${party.id}`);
      toast.success("Party removed");
      fetchParties();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm" data-testid="parties-card">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-slate-900 flex items-center gap-2" style={{ fontFamily: "Outfit, sans-serif" }}>
          <UsersThree weight="bold" className="w-4 h-4 text-slate-500" /> Other parties
          {parties.length > 0 && <span className="text-xs font-medium text-slate-400">({parties.length})</span>}
        </h4>
        {isSolicitor && (
          <Button onClick={() => setEditing({ mode: "add" })} variant="ghost" size="sm" className="h-7 px-2 text-xs gap-1" data-testid="add-party-btn">
            <Plus weight="bold" className="w-3.5 h-3.5" /> Add
          </Button>
        )}
      </div>

      {loading ? (
        <p className="text-xs text-slate-400">Loading…</p>
      ) : parties.length === 0 ? (
        <p className="text-xs text-slate-400" data-testid="parties-empty">
          {isSolicitor ? "No other parties yet — click Add to record other-side details, estate agents, surveyors, etc." : "No other parties listed yet."}
        </p>
      ) : (
        <ul className="space-y-2" data-testid="parties-list">
          {parties.map((p) => {
            const expanded = expandedId === p.id;
            const tone = ROLE_COLORS[p.role] || "bg-slate-100 text-slate-600";
            return (
              <li key={p.id} className="border border-slate-100 rounded-lg p-2.5 bg-slate-50" data-testid={`party-row-${p.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <button
                    type="button"
                    onClick={() => setExpandedId(expanded ? null : p.id)}
                    className="flex-1 min-w-0 text-left"
                    data-testid={`party-expand-${p.id}`}
                  >
                    <span className={`inline-flex text-[10px] font-medium px-1.5 py-0.5 rounded-full ${tone}`}>{p.role}</span>
                    <p className="text-xs font-semibold text-slate-900 mt-1 truncate">
                      {p.name || p.firm || <span className="italic text-slate-400">no name</span>}
                    </p>
                    {p.firm && p.name && (
                      <p className="text-[10px] text-slate-500 truncate">{p.firm}</p>
                    )}
                  </button>
                  {isSolicitor && (
                    <div className="flex items-center gap-0.5 shrink-0">
                      <button onClick={() => setEditing({ party: p, mode: "edit" })} title="Edit" className="w-6 h-6 flex items-center justify-center rounded hover:bg-slate-200 text-slate-400 hover:text-slate-700 transition-colors" data-testid={`party-edit-${p.id}`}>
                        <Pencil weight="bold" className="w-3 h-3" />
                      </button>
                      <button onClick={() => handleDelete(p)} title="Remove" className="w-6 h-6 flex items-center justify-center rounded hover:bg-red-100 text-slate-400 hover:text-red-600 transition-colors" data-testid={`party-delete-${p.id}`}>
                        <Trash weight="bold" className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                {expanded && (
                  <dl className="mt-2 pt-2 border-t border-slate-200 space-y-1.5 text-[11px]" data-testid={`party-details-${p.id}`}>
                    {p.email && (
                      <DetailRow icon={<EnvelopeSimple weight="bold" className="w-3 h-3 text-slate-400" />} label="Email">
                        <a href={`mailto:${p.email}`} className="hover:text-blue-600">{p.email}</a>
                      </DetailRow>
                    )}
                    {p.phone && (
                      <DetailRow icon={<Phone weight="bold" className="w-3 h-3 text-slate-400" />} label="Phone">
                        <a href={`tel:${p.phone}`} className="hover:text-blue-600">{p.phone}</a>
                      </DetailRow>
                    )}
                    {p.firm && (
                      <DetailRow icon={<Buildings weight="bold" className="w-3 h-3 text-slate-400" />} label="Firm" value={p.firm} />
                    )}
                    {p.reference && (
                      <DetailRow icon={<IdentificationBadge weight="bold" className="w-3 h-3 text-slate-400" />} label="Ref" value={p.reference} />
                    )}
                    {p.address && (
                      <DetailRow icon={<MapPin weight="bold" className="w-3 h-3 text-slate-400" />} label="Address" value={p.address} />
                    )}
                    {p.notes && (
                      <div className="pt-1.5 border-t border-slate-100 text-slate-600 whitespace-pre-wrap">{p.notes}</div>
                    )}
                  </dl>
                )}
              </li>
            );
          })}
        </ul>
      )}

      <PartyDialog
        open={!!editing}
        mode={editing?.mode}
        party={editing?.party}
        caseId={caseId}
        roles={roles}
        onClose={() => setEditing(null)}
        onSaved={() => { fetchParties(); setEditing(null); }}
      />
    </div>
  );
}

function DetailRow({ icon, label, value, children }) {
  return (
    <div className="flex items-start gap-2">
      <span className="shrink-0 mt-0.5">{icon}</span>
      <span className="text-slate-500 w-12 shrink-0">{label}</span>
      <span className="text-slate-900 break-words min-w-0">{children ?? value ?? "—"}</span>
    </div>
  );
}

function PartyDialog({ open, mode, party, caseId, roles, onClose, onSaved }) {
  const [form, setForm] = useState({
    role: "", name: "", title: "", first_name: "", middle_name: "", surname: "", firm: "", email: "", phone: "",
    address: "", house_number: "", address_line1: "", address_line2: "", town: "", county: "", postcode: "",
    reference: "", notes: "",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm({
      role: party?.role || "",
      name: party?.name || "",
      title: party?.title || "",
      first_name: party?.first_name || "",
      middle_name: party?.middle_name || "",
      surname: party?.surname || "",
      firm: party?.firm || "",
      email: party?.email || "",
      phone: party?.phone || "",
      address: party?.address || "",
      house_number: party?.house_number || "",
      address_line1: party?.address_line1 || "",
      address_line2: party?.address_line2 || "",
      town: party?.town || "",
      county: party?.county || "",
      postcode: party?.postcode || "",
      reference: party?.reference || "",
      notes: party?.notes || "",
    });
  }, [open, party]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSave = async () => {
    const role = form.role.trim();
    if (!role) { toast.error("Role is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, role };
      // Persist a composed single-string name/address from the individual
      // parts (the inputs only *display* the composition) so letters,
      // SDLT/HMLR and list views stay in sync.
      const composedName = [form.title, form.first_name, form.middle_name, form.surname]
        .map((x) => (x || "").trim()).filter(Boolean).join(" ");
      if (form.first_name || form.surname) payload.name = composedName;
      const composedAddr = [form.house_number, form.address_line1, form.address_line2, form.town, form.county, form.postcode]
        .map((x) => (x || "").trim()).filter(Boolean).join(", ");
      if (composedAddr) payload.address = composedAddr;
      if (mode === "add") {
        await API.post(`/cases/${caseId}/parties`, payload);
        toast.success("Party added");
      } else {
        await API.put(`/cases/${caseId}/parties/${party.id}`, payload);
        toast.success("Party updated");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="party-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit, sans-serif" }}>
            {mode === "edit" ? "Edit party" : "Add party"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Role *</Label>
            <Input
              value={form.role}
              onChange={set("role")}
              list="party-roles"
              placeholder="e.g., Estate agent, Other side's solicitor"
              data-testid="party-role-input"
            />
            <datalist id="party-roles">
              {roles.map((r) => <option key={r} value={r} />)}
            </datalist>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5 space-y-3" data-testid="party-individual-section">
            <p className="text-[11px] font-medium text-slate-500">
              For an individual (e.g. seller / other-side buyer) — captured structurally for SDLT &amp; Land Registry filings.
            </p>
            <div className="grid grid-cols-4 gap-2">
              <div className="space-y-1.5">
                <Label className="text-xs font-medium">Title</Label>
                <Input value={form.title} onChange={set("title")} placeholder="Mr" data-testid="party-title-input" />
              </div>
              <div className="space-y-1.5 col-span-1">
                <Label className="text-xs font-medium">First name</Label>
                <Input value={form.first_name} onChange={set("first_name")} placeholder="Jane" data-testid="party-firstname-input" />
              </div>
              <div className="space-y-1.5 col-span-1">
                <Label className="text-xs font-medium">Middle name(s)</Label>
                <Input value={form.middle_name} onChange={set("middle_name")} placeholder="Anne" data-testid="party-middlename-input" />
              </div>
              <div className="space-y-1.5 col-span-1">
                <Label className="text-xs font-medium">Surname</Label>
                <Input value={form.surname} onChange={set("surname")} placeholder="Smith" data-testid="party-surname-input" />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Name {(form.first_name || form.surname) && <span className="text-slate-400 font-normal">(auto)</span>}</Label>
              <Input value={(form.first_name || form.surname) ? [form.title, form.first_name, form.middle_name, form.surname].filter(Boolean).join(" ") : form.name} onChange={set("name")} disabled={!!(form.first_name || form.surname)} placeholder="Person" data-testid="party-name-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Firm / Company</Label>
              <Input value={form.firm} onChange={set("firm")} placeholder="Firm" data-testid="party-firm-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Email</Label>
              <Input value={form.email} onChange={set("email")} type="email" placeholder="email@example.com" data-testid="party-email-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-medium">Phone</Label>
              <Input value={form.phone} onChange={set("phone")} placeholder="01603 …" data-testid="party-phone-input" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Their reference</Label>
            <Input value={form.reference} onChange={set("reference")} placeholder="e.g., REY/2026/0142" data-testid="party-ref-input" />
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50/60 p-2.5 space-y-2" data-testid="party-address-section">
            <Label className="text-xs font-medium text-slate-500">Address</Label>
            <Input value={form.house_number} onChange={set("house_number")} placeholder="Number (house / flat)" data-testid="party-housenumber-input" />
            <Input value={form.address_line1} onChange={set("address_line1")} placeholder="Address line 1" data-testid="party-address1-input" />
            <Input value={form.address_line2} onChange={set("address_line2")} placeholder="Address line 2 (optional)" data-testid="party-address2-input" />
            <div className="grid grid-cols-2 gap-2">
              <Input value={form.town} onChange={set("town")} placeholder="Town / city" data-testid="party-town-input" />
              <Input value={form.county} onChange={set("county")} placeholder="County (optional)" data-testid="party-county-input" />
            </div>
            <Input value={form.postcode} onChange={set("postcode")} placeholder="Postcode" data-testid="party-postcode-input" />
            {(form.address_line1 || form.town || form.postcode) ? (
              <p className="text-[10px] text-slate-400">Saved as: {[form.house_number, form.address_line1, form.address_line2, form.town, form.county, form.postcode].filter(Boolean).join(", ")}</p>
            ) : (
              <Textarea value={form.address} onChange={set("address")} rows={2} placeholder="Or type a full office / agency address" data-testid="party-address-input" />
            )}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-medium">Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} rows={2} placeholder="Anything else worth noting" data-testid="party-notes-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving || !form.role.trim()} className="text-white" style={{ background: "#100f5d" }} data-testid="party-save-btn">
            {saving ? "Saving…" : (mode === "edit" ? "Save changes" : "Add party")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
